import { section, cart } from "@/glow";
import { remove } from "@fluorescent/dom";
import { emit } from "@/glow/events";
import Carousel from "@/lib/carousel";
import { checkMediaQuery } from "@/lib/media-queries";
import atBreakpointChange from "@/lib/at-breakpoint-change";
import { prefersReducedMotion } from "@/lib/a11y.js";
import { animateShoppableVideos, shouldAnimate } from "@/lib/animation";
import { userIsTabbing } from "@/lib/a11y";

const selectors = {
  swiperContainer: "[js-swiper]",
  swiperSlide: "[js-swiper-slide]",
  nextButton: "[data-next]",
  prevButton: "[data-prev]",
  mediaContainer: "[js-media-container]",
  mediaContainerHasVideo: '[js-media-container][data-has-video="true"]',
  videoElement: "[js-video]",
  muteButton: "[js-mute-button]",
  quickAddButton: '[data-quick-shop-trigger="quick-add"]',
  quickViewButton: '[data-quick-shop-trigger="quick-view"]',
  quickCart: ".quick-cart",
  purchaseConfirmation: ".purchase-confirmation-popup",
};

const classes = {
  loading: "loading",
};

const attrs = {
  blockIndex: "data-block-index",
  columnsTablet: "data-columns-tablet",
  columnsSmallDesktop: "data-columns-small-desktop",
  columnsDesktop: "data-columns-desktop",
  isPlaying: "data-is-playing",
  isMuted: "data-is-muted",
  playInteraction: "data-play-interaction",
  slidesCount: "data-slides-count",
};

section("shoppable-videos", {
  onLoad() {
    this.swiperContainer = this.container.querySelector(
      selectors.swiperContainer
    );
    this.swiperSlides = this.swiperContainer.querySelectorAll(
      selectors.swiperSlide
    );
    this.mediaContainers = Array.from(
      this.container.querySelectorAll(selectors.mediaContainerHasVideo)
    );
    this.videoElements = Array.from(
      this.container.querySelectorAll(selectors.videoElement)
    );
    this.playInteraction = prefersReducedMotion()
      ? "click"
      : this.swiperContainer.getAttribute(attrs.playInteraction);

    this.eventController = new AbortController();
    this.eventOptions = { signal: this.eventController.signal };

    this._initPlayInteractions();
    this._initQuickPurchase();
    this._initCarousel();
    this._initMobileWatcher();

    if (shouldAnimate(this.container)) {
      this.animateShoppableVideos = animateShoppableVideos(this.container);
    }
  },

  onBlockSelect({ target }) {
    const blockIndex = target.getAttribute(attrs.blockIndex);
    this.swiperInstance?.slideTo(blockIndex);
  },

  _loadSectionIO() {
    const observer = new IntersectionObserver(
      this._handleSectionIntersect.bind(this),
      { threshold: [0.3, 0.5] }
    );

    observer.observe(this.container);
    return observer;
  },

  _handleSectionIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
        // on mobile, when play interaction is 'hover', play the active slide's video when scrolled into view.
        if (this.playInteraction === "hover") {
          if (this.isMobile) {
            this._playActiveSlideVideo(this.swiperInstance);
          }
        }
      } else {
        // always pause all videos when scrolled out of view.
        this._pauseAllVideos();
      }
    });
  },

  _initPlayInteractions() {
    this.mediaContainers.forEach((container, index) => {
      if (this.playInteraction === "hover") {
        container.addEventListener(
          "mouseenter",
          e => this._handlePlayInteraction(e, index),
          this.eventOptions
        );

        container.addEventListener(
          "mouseleave",
          e => this._handlePlayInteraction(e, index),
          this.eventOptions
        );
      }

      // We want this "click" listener always present, so that keyboard controls while tabbing will work.
      container.addEventListener(
        "click",
        e => this._handlePlayInteraction(e, index),
        this.eventOptions
      );

      const muteButton = container.querySelector(selectors.muteButton);

      muteButton?.addEventListener(
        "click",
        e => this._handleMuteButton(e, index),
        this.eventOptions
      );
    });
  },

  _initQuickPurchase() {
    this.quickAddButtons = Array.from(
      this.container.querySelectorAll(selectors.quickAddButton)
    );

    this.quickViewButtons = Array.from(
      this.container.querySelectorAll(selectors.quickViewButton)
    );

    this.purchaseConfirmation = document.querySelector(
      selectors.purchaseConfirmation
    );

    this.quickCart = document.querySelector(selectors.quickCart);

    this.quickAddButtons.forEach(button => {
      button.addEventListener(
        "click",
        e => this._handleQuickAddButton(e),
        this.eventOptions
      );
    });

    this.quickViewButtons.forEach(button => {
      button.addEventListener(
        "click",
        e => this._handleQuickViewButton(e),
        this.eventOptions
      );
    });
  },

  _initCarousel() {
    const columnsTablet = parseInt(
      this.swiperContainer.getAttribute(attrs.columnsTablet),
      10
    );
    const columnsSmallDesktop = parseInt(
      this.swiperContainer.getAttribute(attrs.columnsSmallDesktop),
      10
    );
    const columnsDesktop = parseInt(
      this.swiperContainer.getAttribute(attrs.columnsDesktop),
      10
    );

    const slidesCount = parseInt(
      this.swiperContainer.getAttribute(attrs.slidesCount),
      10
    );

    let overflowAmount = 1;

    if (slidesCount > 1) {
      overflowAmount = 1.05; // nice peek amount
    }

    const slidesPerViewMobile = Number(overflowAmount);
    const slidesPerViewTablet = columnsTablet;
    const slidesPerViewSmallDesktop = columnsSmallDesktop;
    const slidesPerViewDesktop = columnsDesktop;

    this.carousel = Carousel(this.swiperContainer, {
      watchSlidesProgress: true,
      grabCursor: true,
      focusableElements: "nothing", // prevent this feature from taking effect
      slidesPerView: slidesPerViewMobile,
      spaceBetween: 13, // matches product grid
      navigation: {
        nextEl: this.container.querySelector(selectors.nextButton),
        prevEl: this.container.querySelector(selectors.prevButton),
      },
      breakpoints: {
        720: {
          spaceBetween: 17, // matches product grid
          slidesPerView: slidesPerViewTablet,
        },
        960: {
          spaceBetween: 17, // matches product grid
          slidesPerView: slidesPerViewSmallDesktop,
        },
        1200: {
          spaceBetween: 25, // matches product grid
          slidesPerView: slidesPerViewDesktop,
        },
      },
      on: {
        afterInit: swiper => this._handleAfterSwiperInitialized(swiper),
        breakpoint: swiper => this._handleSwiperBreakpointChanged(swiper),
        slideChange: swiper => this._handleSlideChanged(swiper),
      },
    });
  },

  _initMobileWatcher() {
    this.isMobile = false;

    this._setIsMobile();
    this.breakPointHandler = atBreakpointChange(
      720,
      this._setIsMobile.bind(this)
    );
  },

  _setIsMobile() {
    this.isMobile = checkMediaQuery("below-720");
  },

  _handleAfterSwiperInitialized(swiper) {
    this.swiperInstance = swiper;
    this.sectionIO = this._loadSectionIO();
  },

  _handleSlideChanged(swiper) {
    if (this.playInteraction === "hover") {
      if (this.isMobile) {
        this._playActiveSlideVideo(swiper);
      }
    } else {
      this._pauseAllVideos();
    }
  },

  _handleSwiperBreakpointChanged(swiper) {
    if (this.playInteraction === "hover") {
      if (this.isMobile) {
        this._playActiveSlideVideo(swiper);
      }
    } else {
      this._pauseAllVideos();
    }
  },

  _handlePlayInteraction(e, index) {
    e.preventDefault();
    e.stopPropagation();

    if (this.playInteraction === "hover" && this.isMobile == false) {
      if (e.type == "mouseenter") this._playVideo(index);
      if (e.type == "mouseleave") this._pauseVideo(index);
    }

    if (
      this.playInteraction === "click" ||
      document.body.classList.contains("user-is-tabbing")
    ) {
      this._toggleVideos(index);
    }
  },

  _handleMuteButton(e, index) {
    e.preventDefault();
    e.stopPropagation();

    if (this.videoElements[index].muted == true) {
      this._unmuteVideo(index);
    } else {
      this._muteVideo(index);
    }
  },

  _handleQuickAddButton(e) {
    const buttonEl = e.currentTarget;
    buttonEl.classList.add(classes.loading);

    // if quick cart and confirmation popup are disabled, use standard form submit
    if (!this.purchaseConfirmation && !this.quickCart) return;

    e.preventDefault();
    e.stopPropagation();
    const { productId } = buttonEl.dataset;

    if (!productId) return;

    const minQuantity = buttonEl.getAttribute("data-quantity-minimum");

    cart.addItemById(productId, minQuantity).then(
      ({ res }) => {
        buttonEl.classList.remove(classes.loading);

        if (this.purchaseConfirmation && !userIsTabbing()) {
          emit("confirmation-popup:open", null, { product: res.items[0] });
        } else {
          emit("quick-cart:updated");
          // Need a delay to allow quick-cart to refresh
          setTimeout(() => {
            emit("quick-cart:open");
          }, 300);
        }
      },
      error => {
        console.error(error);
        remove(buttonEl, "loading");
        alert(error.message);
        emit("quick-cart:open");
      }
    );
  },

  _handleQuickViewButton(e) {
    e.preventDefault();
    e.stopPropagation();
    const buttonEl = e.currentTarget;
    const { productUrl } = buttonEl.dataset;

    if (!productUrl) return;

    emit("quick-view:open", null, {
      productUrl: productUrl,
    });
  },

  _getVideoIndex(element) {
    return this.videoElements.indexOf(element);
  },

  _playActiveSlideVideo(swiper) {
    if (!swiper || swiper.slides.length < 1) return;

    const activeSlideElement = swiper.slides[swiper.activeIndex];
    const activeSlideVideoElement = activeSlideElement.querySelector(
      selectors.videoElement
    );
    const activeSlideVideoIndex = this._getVideoIndex(activeSlideVideoElement);

    this._toggleVideos(activeSlideVideoIndex);
  },

  _playVideo(index) {
    this.videoElements[index].play();
    this.videoElements[index]
      .closest(selectors.mediaContainer)
      .setAttribute(attrs.isPlaying, true);
  },

  _pauseVideo(index) {
    this.videoElements[index].pause();
    this.videoElements[index]
      .closest(selectors.mediaContainer)
      .setAttribute(attrs.isPlaying, false);
  },

  _pauseAllVideos() {
    this.videoElements.forEach((video, index) => {
      this._pauseVideo(index);
    });
  },

  _toggleVideo(index) {
    this.videoElements[index].paused
      ? this._playVideo(index)
      : this._pauseVideo(index);
  },

  _toggleVideos(interactedIndex) {
    if (this.playInteraction === "hover") {
      this.videoElements.forEach((video, index) => {
        index == interactedIndex
          ? this._playVideo(index)
          : this._pauseVideo(index);
      });
    } else {
      this.videoElements.forEach((video, index) => {
        index == interactedIndex
          ? this._toggleVideo(index)
          : this._pauseVideo(index);
      });
    }
  },

  _muteVideo(index) {
    this.videoElements[index].muted = true;
    this.videoElements[index]
      .closest(selectors.mediaContainer)
      .setAttribute(attrs.isMuted, true);
  },

  _unmuteVideo(index) {
    this.videoElements[index].muted = false;
    this.videoElements[index]
      .closest(selectors.mediaContainer)
      .setAttribute(attrs.isMuted, false);
  },

  onUnload() {
    this.breakPointHandler.unload();
    this.swiperInstance?.destroy();
    this.sectionIO?.disconnect();
    this.animateShoppableVideos?.destroy();
    this.eventController?.abort();
  },
});
